import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, X, Loader2, Trash2, Camera, MapPin, Scale,
  ChevronRight, Layers, Download, Pencil, LocateFixed, Upload, Ruler,
  ShieldCheck, FileArchive, ClipboardCheck, Flag, CheckCircle2, Calculator,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { claimTitle } from "@/lib/utils";
import { RunsPanel } from "./RoutePlanner";
import QaModal from "./QaModal";
import LodgementModal from "./LodgementModal";
import AssessmentModal from "./AssessmentModal";
import LineBuilder from "./LineBuilder";
import ImportAssetsModal from "./ImportAssetsModal";
import { CostPanel, CostItemsPanel } from "./CostPanels";
import { PhotoCard, PhotoCaptureModal } from "./PhotoComponents";
import AssetModal from "./AssetModal";
import WorkflowStepper from "./WorkflowStepper";
import { Mono, token, money } from "./ui";

const EMPTY_ASSET = {
  name: "", asset_type: "Road - sealed", funding_measure: "EPAR",
  where_on_asset: "", damage_type: "", treatment_type: "",
  unit_of_measure: "", quantity: "",
  emergency_works: false, restoration_works: false,
  road_name: "", road_id: "", carriageway: "", chainage_start: "", chainage_end: "", site_number: "",
  cet_cost: "", council_cost: "", cost_justification: "", notes: "",
};

const GROUPS = [
  { key: "asset_type", label: "Asset type" },
  { key: "asset_name", label: "Asset name" },
  { key: "damage_type", label: "Damage type" },
  { key: "treatment_type", label: "Treatment type" },
];

const LIST_GROUPS = [
  { key: "funding_measure", label: "Funding measure" },
  { key: "asset_type", label: "Asset type" },
  { key: "damage_type", label: "Damage type" },
  { key: "none", label: "No grouping" },
];

export default function ClaimDetail() {
  const { claimId } = useParams();
  const nav = useNavigate();
  const [claim, setClaim] = useState(null);
  const [meta, setMeta] = useState({ funding_measures: [], phases: [], asset_types: [], treatments: [], regions: [], lga_regions: {} });
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [assetOpen, setAssetOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET);
  const [saveErr, setSaveErr] = useState("");
  const [capture, setCapture] = useState(null);
  const [groupBy, setGroupBy] = useState("funding_measure");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [settingLoc, setSettingLoc] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [driveMsg, setDriveMsg] = useState(null);
  const [qaOpen, setQaOpen] = useState(false);
  const [lodgeOpen, setLodgeOpen] = useState(false);
  const [assessOpen, setAssessOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [acquittalOpen, setAcquittalOpen] = useState(false);
  const [ewSummary, setEwSummary] = useState(null);

  useEffect(() => {
    api.get(`/ew/claims/${claimId}/summary`).then((r) => setEwSummary(r.data)).catch(() => {});
  }, [claimId]);

  const reviewAsset = async (asset, status) => {
    let note = asset.review_note || null;
    if (status === "flagged") {
      note = window.prompt("What needs fixing on this line?", asset.review_note || "");
      if (note === null) return;
    }
    const { data } = await api.post(`/fieldclaim/assets/${asset.id}/review`, { status, note });
    const list = await loadAssets();
    if (selected?.id === asset.id) setSelected(list.find((a) => a.id === asset.id) || data);
  };

  const region = useMemo(() => claim?.cet_region || meta.lga_regions?.[claim?.council] || meta.regions?.[0] || "North Coast", [meta, claim]);

  const loadClaim = useCallback(() => api.get(`/fieldclaim/claims/${claimId}`).then((r) => setClaim(r.data)).catch(() => nav("/dashboard/fieldclaim")), [claimId, nav]);

  const loadAssets = useCallback(() => api.get(`/fieldclaim/claims/${claimId}/assets`).then((r) => {
    setAssets(r.data);
    setSelected((cur) => (cur ? r.data.find((a) => a.id === cur.id) || cur : r.data[0] || null));
    return r.data;
  }).catch(() => []), [claimId]);

  const loadPhotos = useCallback((assetId) => {
    if (!assetId) { setPhotos([]); return; }
    api.get(`/fieldclaim/assets/${assetId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadClaim();
    api.get("/fieldclaim/meta").then((r) => setMeta(r.data)).catch(() => {});
    loadAssets();
  }, [claimId, loadAssets, loadClaim]);

  useEffect(() => { loadPhotos(selected?.id); }, [selected, loadPhotos]);

  const openCreate = () => { setSaveErr(""); setEditAsset(null); setAssetForm(EMPTY_ASSET); setAssetOpen(true); };
  const openEdit = (a) => {
    setSaveErr(""); setEditAsset(a);
    setAssetForm({ ...EMPTY_ASSET, ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v == null ? "" : v])), emergency_works: !!a.emergency_works, restoration_works: !!a.restoration_works });
    setAssetOpen(true);
  };

  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  const buildPayload = () => {
    const editable = [...Object.keys(EMPTY_ASSET), "latitude", "longitude"];
    const numeric = new Set(["quantity", "cet_cost", "council_cost", "latitude", "longitude"]);
    const p = {};
    editable.forEach((k) => {
      if (assetForm[k] === undefined) return;
      p[k] = numeric.has(k) ? numOrNull(assetForm[k]) : assetForm[k];
    });
    return p;
  };

  const saveAsset = async (e) => {
    e.preventDefault();
    setSavingAsset(true); setSaveErr("");
    const payload = buildPayload();
    try {
      if (editAsset) {
        const { data } = await api.patch(`/fieldclaim/assets/${editAsset.id}`, payload);
        const list = await loadAssets(); setSelected(list.find((a) => a.id === data.id) || data);
      } else {
        const { data } = await api.post(`/fieldclaim/claims/${claimId}/assets`, payload);
        const list = await loadAssets(); setSelected(list.find((a) => a.id === data.id) || data);
      }
      setAssetOpen(false); setAssetForm(EMPTY_ASSET); setEditAsset(null);
    } catch (err) {
      setSaveErr(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save asset. Please try again.");
    } finally { setSavingAsset(false); }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Delete this asset and all its evidence?")) return;
    try {
      await api.delete(`/fieldclaim/assets/${id}`);
      if (selected?.id === id) setSelected(null);
      loadAssets();
    } catch { /* asset already gone or transient */ }
  };

  const afterUpload = () => { setCapture(null); loadPhotos(selected?.id); loadAssets(); loadClaim(); };

  const setEventLocation = () => {
    if (!navigator.geolocation) return;
    setSettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.patch(`/fieldclaim/claims/${claimId}`, { latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          await loadClaim();
        } finally { setSettingLoc(false); }
      },
      () => setSettingLoc(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const runExport = (gb) => {
    setExporting(true); setExportOpen(false);
    const url = `${API}/fieldclaim/claims/${claimId}/export/kmz?group_by=${gb}&token=${token()}`;
    const a = document.createElement("a");
    a.href = url; a.download = `${(claim?.name || "claim").replace(/[^a-z0-9]+/gi, "_")}.kmz`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => setExporting(false), 1500);
  };

  const runAcquittal = (withPhotos) => {
    setAcquittalOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/acquittal?token=${token()}${withPhotos ? "&include_photos=1" : ""}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const runEwForm1 = () => {
    setAcquittalOpen(false);
    if (!ewSummary?.scans) {
      setDriveMsg({ ok: false, text: "No EW scans on this claim yet — open EW Prep and use Import from FieldClaim to scan the damage photos first." });
      return;
    }
    const a = document.createElement("a");
    a.href = `${API}/ew/claims/${claimId}/form1?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const runCms = () => {
    setAcquittalOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/cms?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const runAdac = () => {
    setAcquittalOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/adac?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const runWaDfes = () => {
    setAcquittalOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/wa-dfes?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const runSaAudit = () => {
    setAcquittalOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/sa-audit?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const pushDrive = async (kind) => {
    setAcquittalOpen(false);
    setDriveMsg({ busy: true, text: "Saving to Google Drive…" });
    try {
      const { data } = await api.post("/drive/push", { kind, claim_id: claimId });
      setDriveMsg({ ok: true, text: `Saved ${data.file_name} → Drive / ${data.folder}`, link: data.web_link });
    } catch (e) {
      setDriveMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Drive save failed." });
    }
  };

  return (
    <div className="space-y-8 text-white" data-testid="claim-detail-page">
      {driveMsg && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm border px-4 py-3 text-sm shadow-xl bg-navy-900 ${driveMsg.busy ? "border-gold-500/40 text-gold-300" : driveMsg.ok ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`} data-testid="drive-toast">
          <div className="flex items-start gap-2">
            {driveMsg.busy && <Loader2 className="h-4 w-4 animate-spin mt-0.5 shrink-0" />}
            <span>{driveMsg.text}{driveMsg.link && <a href={driveMsg.link} target="_blank" rel="noreferrer" className="block underline text-xs mt-1">Open in Drive</a>}</span>
            <button onClick={() => setDriveMsg(null)} className="ml-2 shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
      <button onClick={() => nav("/dashboard/fieldclaim")} data-testid="back-to-claims" className="inline-flex items-center gap-2 text-white/50 hover:text-gold-400 transition-colors text-sm">
        <ArrowLeft className="h-4 w-4" /> All claims
      </button>

      {claim && (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Mono className="text-gold-400 text-[0.65rem]">{claim.assessing_body || claim.region} · {claim.submission_portal || "—"}</Mono>
            <h1 className="font-heading font-light text-4xl tracking-tight mt-3">{claimTitle(claim)}</h1>
            <div className="flex flex-wrap items-center gap-5 mt-3 text-white/55 font-light text-sm">
              <span className="flex items-center gap-1.5 text-gold-300/80" data-testid="claim-cet-region"><Calculator className="h-3.5 w-3.5" /> CET region: {region}</span>
              {claim.event_date && <span className="font-mono text-xs">{claim.event_date}</span>}
            </div>
            <div className="flex items-center gap-3 mt-3">
              {claim.latitude != null && claim.longitude != null ? (
                <span className="flex items-center gap-1.5 text-emerald-300/90 font-mono text-xs" data-testid="claim-event-point"><MapPin className="h-3.5 w-3.5" /> {Number(claim.latitude).toFixed(5)}, {Number(claim.longitude).toFixed(5)}</span>
              ) : (
                <span className="flex items-center gap-1.5 text-amber-400/80 font-mono text-xs" data-testid="claim-event-point"><MapPin className="h-3.5 w-3.5" /> No event point set</span>
              )}
              <button onClick={setEventLocation} disabled={settingLoc} data-testid="set-event-location" className="inline-flex items-center gap-1.5 text-gold-300 hover:text-gold-200 text-xs disabled:opacity-60">
                {settingLoc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Set to my location
              </button>
            </div>
            <div className="mt-5 max-w-md" data-testid="claim-readiness">
              <div className="flex items-center justify-between mb-1.5">
                <Mono className="text-[0.55rem] text-white/50">Audit readiness · {claim.ready_assets || 0}/{claim.asset_count || 0} assets ready</Mono>
                <span className="font-mono text-sm text-gold-300">{claim.readiness_pct || 0}%</span>
              </div>
              <div className="h-1.5 bg-navy-800 border border-gold-500/15 overflow-hidden">
                <div className={`h-full transition-all ${claim.readiness_pct === 100 ? "bg-emerald-400" : "bg-gold-500"}`} style={{ width: `${claim.readiness_pct || 0}%` }} />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <button onClick={() => setLodgeOpen(true)} data-testid="lodgement-btn" className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 px-4 py-2.5 text-sm transition-colors">
              <ClipboardCheck className="h-4 w-4" /> Lodgement readiness
            </button>
            <button onClick={() => setQaOpen(true)} data-testid="qa-report-btn" className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 px-4 py-2.5 text-sm transition-colors">
              <ShieldCheck className="h-4 w-4" /> QA report
            </button>
            <button onClick={() => setAssessOpen(true)} data-testid="assess-btn" className="inline-flex items-center gap-2 border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 px-4 py-2.5 text-sm transition-colors">
              <Scale className="h-4 w-4" /> Assess
            </button>
            <div className="relative">
              <button onClick={() => setAcquittalOpen((o) => !o)} data-testid="export-acquittal-btn" className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 px-4 py-2.5 text-sm transition-colors">
                <FileArchive className="h-4 w-4" /> Acquittal pack
              </button>
              {acquittalOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setAcquittalOpen(false)} />
                  <div className="absolute right-0 mt-2 w-64 bg-navy-900 border border-emerald-400/30 z-20 shadow-xl">
                    <button onClick={() => runAcquittal(false)} data-testid="acquittal-data-only" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10">
                      Data pack <span className="block text-[0.65rem] text-white/40 font-light">CSVs + evidence manifest (fast)</span>
                    </button>
                    <button onClick={() => runAcquittal(true)} data-testid="acquittal-with-photos" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      Full pack + photos <span className="block text-[0.65rem] text-white/40 font-light">All evidence renamed to QRA/MARS filenames (large)</span>
                    </button>
                    <button onClick={runEwForm1} data-testid="acquittal-ew-form1" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      DTMR EW Form 1 (xlsx) <span className="block text-[0.65rem] text-white/40 font-light">
                        {ewSummary?.scans ? `${ewSummary.scans} AI-scanned damage photo${ewSummary.scans === 1 ? "" : "s"} · ${money(ewSummary.total_ex_gst)} ex GST` : "Emergency Works form · scan damage photos in EW Prep first"}
                      </span>
                    </button>
                    <button onClick={runCms} data-testid="acquittal-cms-csv" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      VIC CMS data CSV <span className="block text-[0.65rem] text-white/40 font-light">EMV Claims Management System · GL-1/GL-3 mapped</span>
                    </button>
                    <button onClick={runAdac} data-testid="acquittal-adac-xml" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      ADAC XML (asset data) <span className="block text-[0.65rem] text-white/40 font-light">IPWEAQ ADAC v6 hand-over · for ADAC-registered councils</span>
                    </button>
                    <button onClick={runWaDfes} data-testid="acquittal-wa-dfes" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      WA DFES data CSV <span className="block text-[0.65rem] text-white/40 font-light">Recovery Funding Submissions & Enquiry Portal · before/after + GPS mapped</span>
                    </button>
                    <button onClick={runSaAudit} data-testid="acquittal-sa-audit" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      SA audited claim pack (ZIP) <span className="block text-[0.65rem] text-white/40 font-light">DPC annual claim · direct-connection auditor manifest</span>
                    </button>
                    <button onClick={() => pushDrive("acquittal")} data-testid="acquittal-drive-push" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      Save data pack to Google Drive <span className="block text-[0.65rem] text-white/40 font-light">Drops into Drive / Managing Disaster Australia / this claim</span>
                    </button>
                    <button onClick={() => pushDrive("adac")} data-testid="adac-drive-push" className="w-full text-left px-4 py-3 text-sm text-white/75 hover:bg-emerald-400/10 border-t border-emerald-400/15">
                      Save ADAC XML to Google Drive <span className="block text-[0.65rem] text-white/40 font-light">Same Drive folder · admin connection required</span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="relative">
            <button onClick={() => setExportOpen((o) => !o)} disabled={exporting} data-testid="export-kmz-btn" className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 px-4 py-2.5 text-sm transition-colors disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export KMZ
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-navy-900 border border-gold-500/30 z-20 shadow-xl" data-testid="export-menu">
                  <div className="px-4 py-2.5 border-b border-gold-500/15"><Mono className="text-[0.5rem] text-white/40">Group placemarks by</Mono></div>
                  {GROUPS.map((g) => (
                    <button key={g.key} onClick={() => runExport(g.key)} data-testid={`export-by-${g.key}`} className="w-full text-left px-4 py-2.5 text-sm text-white/75 hover:bg-gold-500/10 hover:text-gold-300 transition-colors flex items-center gap-2">
                      <Layers className="h-3.5 w-3.5 text-gold-500/60" /> {g.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {claim && <WorkflowStepper claim={claim} onChanged={loadClaim} />}

      {qaOpen && <QaModal claimId={claimId} onClose={() => setQaOpen(false)} />}
      {lodgeOpen && <LodgementModal claimId={claimId} onClose={() => setLodgeOpen(false)} />}
      {assessOpen && <AssessmentModal claimId={claimId} onClose={() => setAssessOpen(false)} />}

      {builderOpen && (
        <LineBuilder claimId={claimId} meta={meta} region={region}
          onClose={() => { setBuilderOpen(false); loadAssets(); loadClaim(); }}
          onSaved={() => { loadAssets(); loadClaim(); }} />
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-8">
        {/* Assets column */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-mono uppercase tracking-[0.2em] text-white/40 text-[0.55rem]" data-testid="lines-approved-count">
              Assets{assets.length > 0 && ` · ${assets.filter((a) => a.review_status === "approved").length}/${assets.length} approved`}
            </span>
            <div className="flex items-center gap-3">
              <button onClick={() => setBuilderOpen(true)} data-testid="line-builder-btn" className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-sm">
                <ClipboardCheck className="h-3.5 w-3.5" /> Line builder
              </button>
              <button onClick={() => setImportOpen(true)} data-testid="import-assets-btn" className="inline-flex items-center gap-1.5 text-white/50 hover:text-gold-300 text-sm">
                <Upload className="h-3.5 w-3.5" /> Import
              </button>
              <button onClick={openCreate} data-testid="add-asset-btn" className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-sm">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>
          {assets.length > 0 && (
            <label className="flex items-center gap-2 border border-gold-500/20 bg-navy-900/40 px-3 py-2">
              <Layers className="h-3.5 w-3.5 text-gold-500/60 shrink-0" />
              <span className="font-mono uppercase tracking-[0.15em] text-[0.5rem] text-white/40 shrink-0">Group by</span>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="group-by-select" className="flex-1 bg-transparent text-white/80 text-xs focus:outline-none cursor-pointer">
                {LIST_GROUPS.map((g) => <option key={g.key} value={g.key} className="bg-navy-900">{g.label}</option>)}
              </select>
            </label>
          )}
          {assets.length === 0 ? (
            <div className="border border-dashed border-gold-500/25 p-8 text-center text-white/40 text-sm font-light">
              No assets yet. Add a road, bridge or culvert to capture evidence against.
            </div>
          ) : (
            <div className="space-y-3">
              {(groupBy === "none"
                ? [["All assets", assets]]
                : Object.entries(assets.reduce((m, a) => { const k = a[groupBy] || "Unclassified"; (m[k] = m[k] || []).push(a); return m; }, {}))
              ).map(([label, items]) => (
                <div key={label} className="border border-gold-500/25">
                  {groupBy !== "none" && (
                    <div className="px-4 py-2 bg-navy-900/60 border-b border-gold-500/15 flex items-center justify-between">
                      <Mono className="text-[0.5rem] text-gold-400/80">{label}</Mono>
                      <span className="font-mono text-[0.55rem] text-white/35">{items.length}</span>
                    </div>
                  )}
                  <div className="divide-y divide-gold-500/10">
                    {items.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setSelected(a)}
                        data-testid={`asset-item-${a.id}`}
                        className={`w-full text-left px-4 py-3.5 flex items-center gap-3 transition-colors ${selected?.id === a.id ? "bg-gold-500/15" : "hover:bg-navy-800"}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm truncate">{a.name}</div>
                          <Mono className="text-[0.5rem] text-white/40">{a.asset_type} · {a.funding_measure} · {a.photo_total} photos</Mono>
                        </div>
                        {a.review_status === "approved" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" data-testid={`review-chip-approved-${a.id}`} />}
                        {a.review_status === "flagged" && <Flag className="h-3.5 w-3.5 text-red-400 shrink-0" data-testid={`review-chip-flagged-${a.id}`} />}
                        <span className={`font-mono text-[0.5rem] px-1.5 py-0.5 border shrink-0 ${a.readiness?.pct === 100 ? "text-emerald-300 border-emerald-500/40" : "text-gold-300 border-gold-500/40"}`} data-testid={`asset-readiness-${a.id}`}>{a.readiness?.pct ?? 0}%</span>
                        <ChevronRight className={`h-4 w-4 shrink-0 ${selected?.id === a.id ? "text-gold-400" : "text-white/25"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <RunsPanel claimId={claimId} />
        </div>

        {/* Detail column */}
        <div>
          {!selected ? (
            <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light h-full flex items-center justify-center">
              Select or add an asset to begin capturing 3-phase evidence.
            </div>
          ) : (
            <div className="space-y-6" data-testid="asset-evidence">
              <div className="flex items-start justify-between gap-4 border-b border-gold-500/20 pb-4">
                <div>
                  <h2 className="font-heading text-2xl">{selected.name}</h2>
                  <Mono className="text-[0.55rem] text-white/50 mt-1 block">
                    {selected.asset_type} · {selected.funding_measure}
                    {selected.where_on_asset && ` · ${selected.where_on_asset}`}
                    {selected.chainage_start && ` · Ch ${selected.chainage_start}${selected.chainage_end ? `–${selected.chainage_end}` : ""}`}
                  </Mono>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {selected.emergency_works && <span className="font-mono text-[0.5rem] uppercase tracking-wider px-2 py-0.5 border border-red-500/40 text-red-300" data-testid="badge-emergency">Emergency works</span>}
                    {selected.restoration_works && <span className="font-mono text-[0.5rem] uppercase tracking-wider px-2 py-0.5 border border-blue-400/40 text-blue-300" data-testid="badge-restoration">Restoration works</span>}
                    {selected.treatment_type && <span className="font-mono text-[0.5rem] uppercase tracking-wider px-2 py-0.5 border border-gold-500/40 text-gold-300">{selected.treatment_type}</span>}
                    {selected.chainage_extent && (
                      <span className="font-mono text-[0.5rem] uppercase tracking-wider px-2 py-0.5 border border-sky-400/40 text-sky-300 inline-flex items-center gap-1" data-testid="chainage-extent">
                        <Ruler className="h-3 w-3" /> Ch {selected.chainage_extent.start}–{selected.chainage_extent.finish} · {selected.chainage_extent.length_m}m
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => openEdit(selected)} data-testid={`edit-asset-${selected.id}`} className="text-white/40 hover:text-gold-400"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => deleteAsset(selected.id)} data-testid={`delete-asset-${selected.id}`} className="text-white/30 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              {/* line-by-line review */}
              <div className={`border p-4 flex items-start justify-between gap-4 flex-wrap ${selected.review_status === "approved" ? "border-emerald-400/30 bg-emerald-400/5" : selected.review_status === "flagged" ? "border-red-500/35 bg-red-500/5" : "border-gold-500/20 bg-navy-900/40"}`} data-testid="line-review-panel">
                <div className="min-w-0">
                  <Mono className="text-[0.55rem] text-white/50 flex items-center gap-1.5"><ClipboardCheck className="h-3 w-3 text-gold-500/60" /> Line review</Mono>
                  <div className="mt-1.5 text-sm">
                    {selected.review_status === "approved" && <span className="text-emerald-300 flex items-center gap-1.5" data-testid="review-status-approved"><CheckCircle2 className="h-4 w-4" /> Approved{selected.reviewed_by_name ? ` by ${selected.reviewed_by_name}` : ""}{selected.reviewed_at ? ` · ${selected.reviewed_at.slice(0, 10)}` : ""}</span>}
                    {selected.review_status === "flagged" && <span className="text-red-300 flex items-center gap-1.5" data-testid="review-status-flagged"><Flag className="h-4 w-4" /> Flagged{selected.reviewed_by_name ? ` by ${selected.reviewed_by_name}` : ""}</span>}
                    {(!selected.review_status || selected.review_status === "pending") && <span className="text-white/50" data-testid="review-status-pending">Awaiting line review — approve or flag before submission.</span>}
                  </div>
                  {selected.review_note && <p className="text-white/55 text-xs font-light mt-1" data-testid="review-note">“{selected.review_note}”</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {selected.review_status !== "approved" && (
                    <button onClick={() => reviewAsset(selected, "approved")} data-testid={`approve-line-${selected.id}`} className="inline-flex items-center gap-1.5 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 px-3 py-2 text-xs transition-colors"><CheckCircle2 className="h-3.5 w-3.5" /> Approve line</button>
                  )}
                  {selected.review_status !== "flagged" && (
                    <button onClick={() => reviewAsset(selected, "flagged")} data-testid={`flag-line-${selected.id}`} className="inline-flex items-center gap-1.5 border border-red-400/50 text-red-300 hover:bg-red-400/10 px-3 py-2 text-xs transition-colors"><Flag className="h-3.5 w-3.5" /> Flag</button>
                  )}
                  {selected.review_status && selected.review_status !== "pending" && (
                    <button onClick={() => reviewAsset(selected, "pending")} data-testid={`reset-review-${selected.id}`} className="border border-white/20 text-white/50 hover:text-white px-3 py-2 text-xs transition-colors">Reset</button>
                  )}
                </div>
              </div>

              <CostPanel asset={selected} region={region} onEdit={() => openEdit(selected)} />

              {selected.readiness && (
                <div className="border border-gold-500/20 bg-navy-900/40 p-4" data-testid="readiness-checklist">
                  <div className="flex items-center justify-between mb-3">
                    <Mono className="text-[0.55rem] text-white/50">Audit readiness · QRA / NSWRA evidence</Mono>
                    <span className={`font-mono text-sm ${selected.readiness.pct === 100 ? "text-emerald-300" : "text-gold-300"}`}>{selected.readiness.pct}%</span>
                  </div>
                  {selected.readiness.missing.length === 0 && (
                    <div className="text-xs text-emerald-300/90 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> All mandatory evidence checks passed.</div>
                  )}
                  <ul className="space-y-1.5">
                    {selected.readiness.missing.map((m) => (
                      <li key={m} className="flex items-center gap-2 text-xs text-red-300/90"><span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" /> {m}</li>
                    ))}
                    {selected.readiness.advisories.map((m) => (
                      <li key={m} className="flex items-center gap-2 text-xs text-amber-300/80"><span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" /> {m} <span className="text-white/30">· recommended</span></li>
                    ))}
                  </ul>
                </div>
              )}

              <CostItemsPanel assetId={selected.id} claimId={claimId} meta={meta} region={region} />

              {meta.phases.map((ph) => {
                const phasePhotos = photos.filter((p) => p.phase === ph.code);
                return (
                  <div key={ph.code} data-testid={`phase-${ph.code}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Mono className="text-gold-400 text-[0.6rem]">{ph.name}</Mono>
                        <span className="font-mono text-[0.6rem] text-white/40">{phasePhotos.length}</span>
                      </div>
                      <label data-testid={`capture-${ph.code}`} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-gold-400 cursor-pointer transition-colors">
                        <Camera className="h-4 w-4" /> Capture
                        <input
                          type="file" accept="image/*" capture="environment" className="hidden"
                          onChange={(e) => { if (e.target.files[0]) setCapture({ file: e.target.files[0], phase: ph.code }); e.target.value = ""; }}
                        />
                      </label>
                    </div>
                    {phasePhotos.length === 0 ? (
                      <div className="border border-dashed border-gold-500/15 p-6 text-center text-white/30 text-xs font-light">No photos captured for this phase.</div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {phasePhotos.map((p) => <PhotoCard key={p.id} photo={p} onDelete={afterUpload} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {assetOpen && (
        <AssetModal
          form={assetForm} setForm={setAssetForm} meta={meta} region={region} isEdit={!!editAsset} error={saveErr}
          saving={savingAsset} onClose={() => { setAssetOpen(false); setEditAsset(null); setSaveErr(""); }} onSubmit={saveAsset}
        />
      )}
      {capture && (
        <PhotoCaptureModal
          capture={capture} assetId={selected.id} phaseName={meta.phases.find((p) => p.code === capture.phase)?.name}
          defaultChainage={selected.chainage_start || ""}
          onClose={() => setCapture(null)} onUploaded={afterUpload}
        />
      )}
      {importOpen && (
        <ImportAssetsModal claimId={claimId} onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); loadAssets(); }} />
      )}
    </div>
  );
}
